import { useEffect, useState, useRef, type FunctionComponent } from 'react';
import PlayButton from './PlayButton';
import RecenterButton from './RecenterButton';
import { SpeedSwitch } from './SpeedSwitch';
import { ButtonsMenu } from '../../ButtonsMenu';
import { speeds } from '../constants';
import LiveViewButton from '../../LiveViewButton';
import { Timestamp } from '../../../services/Timestamp';

import { Page, type VesselViewModel } from '../../../types';
import PickTime from '../../PickTime/PickTime';
import ShareLink from '../../Map/components/ShareLink/ShareLink';
import { useDetectMobileScreenWidth } from '../../../hooks/useDetectMobileScreenWidth';
import IncorrectInformation from '../../Map/components/IncorrectInformation/IncorrectInformation';

interface Props {
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  vessel: VesselViewModel;
  timestamp: Timestamp;
  timezone: string;
  operation: string;
  isTripsPanelOpen: boolean;
  setTimestamp: (timestamp: Timestamp) => void;
  setStartTimestamp: (timestamp: Timestamp) => void;
  switchToLiveview: () => void;
  isLockedOnTarget: boolean;
  onTargetLock: (mmsi: string) => void;
  jobStartTimestamp: Timestamp;
}
const ScrubberControls: FunctionComponent<Props> = ({
  isPlaying,
  setIsPlaying,
  vessel,
  timestamp,
  timezone,
  operation,
  isTripsPanelOpen,
  setTimestamp,
  switchToLiveview,
  setStartTimestamp,
  isLockedOnTarget,
  onTargetLock,
  jobStartTimestamp,
}) => {
  const [currentSpeed, setCurrentSpeed] = useState(speeds[1]);
  const timeout = useRef<NodeJS.Timeout>(null);
  const isMobileView = useDetectMobileScreenWidth();
  const page = Page.Playback;

  useEffect(() => {
    if (isPlaying) {
      if (timeout.current) {
        clearInterval(timeout.current);
      }
      timeout.current = setInterval(() => {
        const newTimestamp = timestamp.add(Timestamp.fromSeconds(currentSpeed.refreshInterval));
        setTimestamp(newTimestamp);
      }, currentSpeed.timeoutInMilliseconds);
    }

    return (): void => {
      if (timeout.current) {
        clearInterval(timeout.current);
        timeout.current = null;
      }
    };
  }, [isPlaying, currentSpeed, timestamp, setTimestamp]);

  const miscellaneousButtons = (
    <>
      <ShareLink
        timestamp={timestamp}
        startTimestamp={jobStartTimestamp}
        targetVesselMmsi={vessel?.mmsi}
        page={page}
        timezone={timezone}
      />
      <IncorrectInformation page={page} vessel={vessel} timestamp={timestamp} timezone={timezone} />
    </>
  );

  return (
    <div className="scrubber-controls-container">
      <PlayButton isPlaying={isPlaying} setIsPlaying={setIsPlaying} timestamp={timestamp} vessel={vessel} />
      <SpeedSwitch
        currentSpeed={currentSpeed}
        timestamp={timestamp}
        vessel={vessel}
        setCurrentSpeed={setCurrentSpeed}
      />
      <RecenterButton vessel={vessel} isLockedOnTarget={isLockedOnTarget} onTargetLock={onTargetLock} />
      <PickTime
        isTripsPanelOpen={isTripsPanelOpen}
        operation={operation}
        page={page}
        timestamp={timestamp}
        timezone={timezone}
        vessel={vessel}
        setTimestamp={setTimestamp}
        setStartTimestamp={setStartTimestamp}
      />
      <LiveViewButton switchToLiveview={switchToLiveview} operation={operation} />
      {isMobileView ? <ButtonsMenu>{miscellaneousButtons}</ButtonsMenu> : miscellaneousButtons}
    </div>
  );
};

export default ScrubberControls;
